import { Suspense } from 'react';

import { Constants } from '@constants';
import {
    IconExclamationTriangleFilled,
    IconInfoCircleOutline
} from '@iwoca/icons';
import { fetchPostCreditCardActivateAccount } from '@iwoca/lapi-client/edge';
import { AlertBox, Button, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useMutation } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { useGetDeliveryAddressData } from './useGetDeliveryAddressData';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { ACCOUNT_CONFIRMATION_URL } from '../../constants';
import { useGetCreditCardOffer } from '../../hooks/useGetCreditCardOffer';

export const ConfirmDeliveryAddress = () => (
    <ErrorBoundary fallback={(error) => <FallbackErrorModal error={error} />}>
        <Suspense
            fallback={<MainContentPending data-testid="loading-fallback" />}
        >
            <div className="my-2xl">
                <Text className="text-3xl font-bold">
                    <FormattedMessage
                        id="_.credit_card.onboarding.delivery.title"
                        defaultMessage="All signed! One last thing..."
                    />
                </Text>
                <Text className="my-m text-xl font-reg">
                    <FormattedMessage
                        id="_.credit_card.onboarding.delivery.description"
                        defaultMessage="We'll be sending your card to the address below. Hit confirm to activate and send off your card."
                    />
                </Text>
            </div>

            <ErrorBoundary
                fallback={(error) => <FallbackErrorModal error={error} />}
            >
                <ResidentialAddress />
            </ErrorBoundary>
        </Suspense>
    </ErrorBoundary>
);

const ResidentialAddress = () => {
    const history = useHistory();
    const navigateToConfirmationPage = () => {
        history.push(maintainStateKey({ pathname: ACCOUNT_CONFIRMATION_URL }));
    };

    const { creditCardOffer } = useGetCreditCardOffer({ suspense: true });
    const stateKey = useStateKey();

    const {
        mutate: activateAccount,
        isLoading: isActivateAccountLoading,
        isError: isActivateAccountError
    } = useMutation({
        mutationFn: () =>
            fetchPostCreditCardActivateAccount({
                stateKey,
                offerId: creditCardOffer!.id!
            }),
        onSuccess: () => navigateToConfirmationPage()
    });

    const { residential_address } = useGetDeliveryAddressData();

    if (!residential_address) {
        return (
            <>
                <Text className="mb-m mt-xl flex flex-row gap-l text-m font-bold">
                    <IconExclamationTriangleFilled
                        className="my-auto fill-error"
                        fontSize={20}
                    />
                    <FormattedMessage
                        defaultMessage="Unverified address"
                        id="_.credit_card.onboarding.delivery.address.error"
                    />
                </Text>
                <Text className="text-m font-reg">
                    <FormattedMessage
                        defaultMessage="We're unable to verify your residential address. Please call us on <a>{phone}</a>"
                        id="_.credit_card.onboarding.delivery.address.error.description"
                        values={{
                            phone: Constants.CREDIT_CARD_PHONE_NUMBER.display,
                            a: (chunks) => (
                                <a
                                    href={
                                        Constants.CREDIT_CARD_PHONE_NUMBER.tel
                                    }
                                >
                                    <strong>{chunks}</strong>
                                </a>
                            )
                        }}
                    />
                </Text>
            </>
        );
    }

    return (
        <>
            <AlertBox className="my-auto flex flex-row gap-s">
                <IconInfoCircleOutline className="my-auto" />
                <Text className="text-s font-med">
                    <FormattedMessage
                        defaultMessage="Your virtual card will be available to use immediately."
                        id="_.credit_card.onboarding.delivery.info"
                    />
                </Text>
            </AlertBox>
            <div className="my-3xl flex flex-col">
                <Text className="text-l font-bold">
                    <FormattedMessage
                        defaultMessage="ID verified address"
                        id="_.credit_card.onboarding.delivery.address_title"
                    />
                </Text>
                <Text className="text-m font-reg">
                    {residential_address.house_number}
                    {residential_address.house_number &&
                        residential_address.street_line_1 &&
                        /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
                        ' '}
                    {residential_address.street_line_1}
                    {residential_address.street_line_1 &&
                        residential_address.town &&
                        /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
                        ', '}
                    {residential_address.town}
                </Text>
                <Text className="text-m font-reg">
                    {residential_address.postcode}
                </Text>
            </div>

            <Button
                variant="primary"
                loading={isActivateAccountLoading}
                onClick={() => activateAccount()}
            >
                <FormattedMessage
                    id="_.credit_card.onboarding.delivery.confirm_and_activate"
                    defaultMessage="Confirm and activate card"
                />
            </Button>

            {isActivateAccountError && (
                <Text className="mt-m text-m font-reg text-error">
                    <FormattedMessage
                        id="_.credit_card.onboarding.delivery.address.confirmation.error"
                        defaultMessage="There was an error activating your account. Please call us on <a>{phone}</a>."
                        values={{
                            phone: Constants.CREDIT_CARD_PHONE_NUMBER.display,
                            a: (chunks) => (
                                <a
                                    href={
                                        Constants.CREDIT_CARD_PHONE_NUMBER.tel
                                    }
                                >
                                    <strong>{chunks}</strong>
                                </a>
                            )
                        }}
                    />
                </Text>
            )}
        </>
    );
};
